import * as React from 'react'

import { CheckIcon, SearchIcon } from 'lucide-react'
import { classList } from 'react-classlist-helper'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import { DASH } from '~/client/src/shared/utils/usefulStrings'

interface IMultiSelectItem {
  id: string
  caption: string
}

interface IProps {
  items: IMultiSelectItem[]
  selectedIds: string[]
  onItemToggle: (item: IMultiSelectItem) => void
  onSelectAll: (filteredItems: IMultiSelectItem[]) => void
  onDeselectAll: (filteredItems: IMultiSelectItem[]) => void
  title?: string
  className?: string
  itemCountLabel?: (count: number) => string
  showSearch?: boolean
  searchQuery?: string
  onSearchChange?: (query: string) => void
}

interface IState {
  internalSearchQuery: string
}

export default class MultiSelectDropdown extends React.Component<
  IProps,
  IState
> {
  public constructor(props: IProps) {
    super(props)
    this.state = {
      internalSearchQuery: '',
    }
  }

  private getSearchQuery(): string {
    return this.props.searchQuery !== undefined
      ? this.props.searchQuery
      : this.state.internalSearchQuery
  }

  private getFilteredItems(): IMultiSelectItem[] {
    const { items } = this.props
    const searchQuery = this.getSearchQuery()

    if (!searchQuery.trim()) {
      return items
    }

    const query = searchQuery.toLowerCase()
    return items.filter(item => item.caption.toLowerCase().includes(query))
  }

  private handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (this.props.onSearchChange) {
      this.props.onSearchChange(value)
    } else {
      this.setState({ internalSearchQuery: value })
    }
  }

  private renderSelectAllSection = (): JSX.Element => {
    const { selectedIds, onSelectAll, onDeselectAll, itemCountLabel } =
      this.props

    const filteredItems = this.getFilteredItems()
    const filteredSelectedIds = selectedIds.filter(id =>
      filteredItems.some(item => item.id === id),
    )

    const allFilteredSelected =
      filteredItems.length > 0 &&
      filteredSelectedIds.length === filteredItems.length

    const label = itemCountLabel
      ? itemCountLabel(filteredItems.length)
      : `${filteredItems.length} ${Localization.translator.fields || 'fields'}`

    return (
      <div className="row gap-10">
        <div
          className="row x-center no-grow mw14 mh14 ba-grey-scale-light brada2 pointer"
          onClick={e => {
            e.stopPropagation()
            if (allFilteredSelected) {
              onDeselectAll(filteredItems)
            } else {
              onSelectAll(filteredItems)
            }
          }}
        >
          {allFilteredSelected && (
            <span className="text large bold brand-dark center">{DASH}</span>
          )}
        </div>
        <span className="text large medium-bold grey">{label}</span>
      </div>
    )
  }

  private renderSearchBox = (): JSX.Element => {
    if (this.props.showSearch === false) return null

    return (
      <div className="row h40 px5 py10 ba-palette-brand-lighter brada4 x-end no-grow">
        <SearchIcon size={12} className="tw:text-muted-foreground" />
        <input
          type="text"
          placeholder={Localization.translator.search}
          value={this.getSearchQuery()}
          onChange={this.handleSearchChange}
          className="text large medium-bold brand-dark no-outline bg-transparent ba-none mw100"
          onClick={e => e.stopPropagation()}
        />
      </div>
    )
  }

  private renderItem = (item: IMultiSelectItem): JSX.Element => {
    const { selectedIds, onItemToggle } = this.props
    const isSelected = selectedIds.includes(item.id)

    return (
      <div
        key={item.id}
        className={classList({
          'row gap-10 py8 px5 pointer bb-palette-brand-lighter dropdown-item': true,
          selected: isSelected,
        })}
        onClick={e => {
          e.stopPropagation()
          onItemToggle(item)
        }}
      >
        <div
          className={classList({
            'row x-center no-grow mw14 mh14 ba-grey-scale-light brada2': true,
            'bg-blue-40 ba-none': isSelected,
          })}
        >
          {isSelected && (
            <CheckIcon size={10} className="tw:text-primary-foreground" />
          )}
        </div>
        <span className="row text large medium-bold brand-dark">
          {item.caption}
        </span>
      </div>
    )
  }

  public render(): JSX.Element {
    const { title, className } = this.props
    const filteredItems = this.getFilteredItems()

    return (
      <div
        className={classList({
          'absolute top0 left-100 ml8 mw300 bg-white ba-palette-brand-lighter brada4 z-index-1000 beautiful-shadow': true,
          [className]: !!className,
        })}
      >
        {title && (
          <div className="text large bold brand-dark center py10 bb-palette-brand-lighter px12">
            {title}
          </div>
        )}
        <div className="row x-between py10 px5 bb-palette-brand-lighter">
          {this.renderSelectAllSection()}
          {this.renderSearchBox()}
        </div>
        <div className="max-height360 overflow-y-auto">
          {filteredItems.length > 0 ? (
            filteredItems.map(item => this.renderItem(item))
          ) : (
            <div className="row py12 px12">
              <span className="text large medium-bold grey">
                {Localization.translator.noResults || 'No results found'}
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }
}
